import { Link } from "wouter";
import { Shield, FileText, Cookie, Scale } from "lucide-react";

interface FooterProps {
  t: (key: string) => string;
  dir: "ltr" | "rtl";
}

const SPECIALTY_LINKS = [
  { href: "/specialties/vision", key: "footer.specialtyVision" },
  { href: "/specialties/dental", key: "footer.specialtyDental" },
  { href: "/specialties/hair", key: "footer.specialtyHair" },
  { href: "/specialties/aesthetic", key: "footer.specialtyAesthetic" },
];

const INFO_LINKS = [
  { href: "/#section-journey", key: "footer.journey" },
  { href: "/#section-faq", key: "footer.faq" },
  { href: "/#section-testimonials", key: "footer.testimonials" },
];

const legalLinks = (t: (k: string) => string) => [
  { href: "/legal/mentions-legales", label: t("footer.mentionsLegales"), icon: FileText },
  { href: "/legal/privacy-policy", label: t("footer.privacyPolicy"), icon: Shield },
  { href: "/legal/terms-of-service", label: t("footer.termsOfService"), icon: Scale },
  { href: "/legal/cookies", label: t("footer.cookies"), icon: Cookie },
];

export function Footer({ t, dir }: FooterProps) {
  const year = new Date().getFullYear();

  return (
    <footer
      className="relative mt-auto border-t border-primary/20 bg-foreground/[0.05]"
      dir={dir}
      data-testid="footer"
    >
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Column 1: Brand */}
          <div className="space-y-3">
            <img
              src="/images/logo_footer_hd.png"
              alt="Aurelian Private Care"
              className="h-14 w-auto opacity-80"
              data-testid="text-footer-brand"
            />
            <p className="text-sm text-muted-foreground max-w-xs leading-relaxed" data-testid="text-footer-tagline">
              {t("footer.tagline")}
            </p>
            <p className="text-xs text-muted-foreground/80">
              &copy; {year} {t("footer.brand")}
            </p>
          </div>

          {/* Column 2: Specialties */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-foreground uppercase tracking-wider">
              {t("footer.specialtiesTitle")}
            </h4>
            <nav className="flex flex-col gap-2" aria-label="Specialties">
              {SPECIALTY_LINKS.map(({ href, key }) => (
                <Link key={href} href={href}>
                  <span className="text-sm text-muted-foreground hover:text-primary transition-colors cursor-pointer">
                    {t(key)}
                  </span>
                </Link>
              ))}
            </nav>
          </div>

          {/* Column 3: Information */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-foreground uppercase tracking-wider">
              {t("footer.infoTitle")}
            </h4>
            <nav className="flex flex-col gap-2" aria-label="Information">
              {INFO_LINKS.map(({ href, key }) => (
                <a key={href} href={href}>
                  <span className="text-sm text-muted-foreground hover:text-primary transition-colors cursor-pointer">
                    {t(key)}
                  </span>
                </a>
              ))}
            </nav>
          </div>

          {/* Column 4: Legal */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-foreground uppercase tracking-wider" data-testid="text-footer-legal-title">
              {t("footer.legal")}
            </h4>
            <nav className="flex flex-col gap-2" aria-label="Legal navigation">
              {legalLinks(t).map((link) => (
                <Link key={link.href} href={link.href}>
                  <span
                    className="group flex items-center gap-2 text-sm text-muted-foreground transition-colors duration-200 hover:text-primary cursor-pointer"
                    data-testid={`link-footer-${link.href.split("/").pop()}`}
                  >
                    <link.icon className="w-3.5 h-3.5 opacity-50 group-hover:opacity-100 transition-opacity" />
                    {link.label}
                  </span>
                </Link>
              ))}
            </nav>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-border/20">
          <p className="text-xs text-muted-foreground/70 text-center leading-relaxed" data-testid="text-footer-disclaimer">
            {t("footer.disclaimer")}
          </p>
          <p className="text-xs text-muted-foreground/50 text-center mt-2" data-testid="text-footer-rights">
            {t("footer.rights")}
          </p>
        </div>
      </div>
    </footer>
  );
}
